import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  createSortableColumn,
  DataTable,
  type TanstackReactTable,
} from "@devhop/ui";
import { EyeIcon } from "lucide-react";
import { formatDateTimeLocal } from "@/shared/lib/date-time";
import { getInitials } from "@/shared/lib/utils";
import { resolveImageSrc } from "@/shared/ui/AppImage";
import { RowActions } from "@/shared/ui/RowActions";
import type { MechanicListItem } from "../api/client";

type MechanicsTableProps = {
  data: MechanicListItem[];
  isLoading?: boolean;
  offset: number;
  limit: number;
  totalCount: number;
  onPaginationChange: (offset: number, limit: number) => void;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  onSortingChange?: (id: string, desc: boolean) => void;
  onViewDetails?: (id: string) => void;
};

const STATUS_STYLE: Record<
  string,
  { variant: "secondary" | "default" | "muted"; label: string }
> = {
  pending_verification: {
    variant: "secondary",
    label: "ລໍຖ້າກວດສອບ",
  },
  active: { variant: "default", label: "ໃຊ້ງານປົກກະຕິ" },
  suspended: { variant: "muted", label: "ລະງັບ" },
};

function formatIdCard(value: string | null): string {
  if (!value) return "—";
  const digits = value.replace(/\D/g, "");
  if (digits.length >= 9) {
    return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6, 9)}`;
  }
  return value;
}

export function MechanicsTable({
  data,
  isLoading,
  offset,
  limit,
  totalCount,
  onPaginationChange,
  sortBy,
  sortOrder,
  onSortingChange,
  onViewDetails,
}: MechanicsTableProps) {
  const columns: TanstackReactTable.ColumnDef<MechanicListItem>[] = [
    createSortableColumn<MechanicListItem>("displayName", "ຊື່ຊ່າງ", {
      size: 220,
      cell: ({ row }) => {
        const m = row.original;
        const name = m.displayName || m.userId || "—";
        const initials = m.displayName
          ? getInitials(m.displayName)
          : m.userId.slice(0, 2).toUpperCase();
        const avatarSrc = resolveImageSrc(m.image);
        return (
          <div className="flex items-center gap-3">
            <Avatar className="size-10 shrink-0">
              <AvatarImage
                src={avatarSrc ?? undefined}
                alt={name}
                className="object-cover"
              />
              <AvatarFallback className="bg-secondary/20 font-medium text-secondary text-sm">
                {initials}
              </AvatarFallback>
            </Avatar>
            <div className="min-w-0">
              <div className="truncate font-medium">{name}</div>
              <div className="text-muted-foreground text-xs">
                UID {m.userId}
              </div>
            </div>
          </div>
        );
      },
    }),
    {
      id: "idCardNo",
      header: "ເລກບັດປະຈຳຕົວ",
      cell: ({ row }) => (
        <span className="font-mono text-sm">
          {formatIdCard(row.original.idCardNo)}
        </span>
      ),
      size: 140,
    },
    createSortableColumn<MechanicListItem>("createdAt", "ວັນທີລົງທະບຽນ", {
      size: 160,
      cell: ({ row }) => (
        <span className="text-muted-foreground text-sm">
          {formatDateTimeLocal(row.original.createdAt)}
        </span>
      ),
    }),
    {
      id: "status",
      header: "ສະຖານະ",
      cell: ({ row }) => {
        const status = row.original.status ?? "pending_verification";
        const style = STATUS_STYLE[status] ??
          STATUS_STYLE.pending_verification ?? {
            variant: "secondary" as const,
            label: "ລໍຖ້າກວດສອບ",
          };
        return (
          <Badge
            variant={
              style.variant as "secondary" | "default" | "muted" | "destructive"
            }
          >
            {style.label}
          </Badge>
        );
      },
      size: 140,
    },
    {
      id: "actions",
      header: "",
      cell: ({ row }) => {
        if (!onViewDetails) return "—";
        const actions = [
          {
            label: "ເບິ່ງລາຍລະອຽດ",
            icon: <EyeIcon className="h-4 w-4" />,
            onClick: () => onViewDetails(row.original.id),
          },
        ];
        return <RowActions actions={actions} maxInline={2} />;
      },
      size: 120,
    },
  ];

  return (
    <DataTable<MechanicListItem, unknown>
      noDataMessage="ບໍ່ພົບຊ່າງແປງ"
      isLoading={isLoading}
      columns={columns}
      data={data}
      offset={offset}
      limit={limit}
      totalCount={totalCount}
      onPaginationChange={(p) => onPaginationChange(p.offset, p.limit)}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSortingChange={(sorting) => {
        if (sorting[0]?.id === "" || !onSortingChange) return;
        onSortingChange(sorting[0]?.id as string, !!sorting[0]?.desc);
      }}
    />
  );
}
